/**
 * ## Performance:
 * - 5 DOM layers with CSS @keyframes (no JS in hot path)
 * - Shared NebulaCoordinator singleton (1 IntersectionObserver for all instances)
 * - Visibility gating via CSS class toggle (`paused`/`running`)
 * - `contain: strict` + `translateZ(0)` for compositor promotion
 * - `animation-play-state` pauses when off-screen or tab hidden
 *
 * ## Visual layers (back to front):
 * 1. Cosmic Dust + Void — static dark filaments, vignette, void pockets (multiply, zero animation cost)
 * 2. Chromatic Red — aberration channel shifted right (screen blend)
 * 3. Chromatic Blue — aberration channel shifted left (screen blend)
 * 4. Iridescent Core + Event Horizon — hue-rotate cycling center (screen blend)
 * 5. Bioluminescent Tendrils — organic breathing gas wisps (screen blend)
 */

import type { ElementPart } from 'lit'
import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { EMPTY, Observable, type Subscription, animationFrameScheduler, from, fromEvent, merge, of, timer } from 'rxjs'
import { catchError, observeOn, take, tap } from 'rxjs/operators'
import { SPRING_GENTLE, SPRING_SMOOTH, getEasing } from '@schmancy/utils/animation'
import { reducedMotion$ } from './reduced-motion'

export interface NebulaOptions {
	/** Whether the nebula is active/visible. Default: true (auto-shows, auto-hides after autoHideDuration) */
	active?: boolean

	/** Auto-hide after this duration (ms). Set to 0 to disable. Default: 3000 */
	autoHideDuration?: number

	/** Render behind content instead of on top. Default: true */
	background?: boolean

	/** Overall brightness/intensity (0-1). Default: 1 */
	intensity?: number

	/** Blur amount multiplier. Default: 1 */
	blur?: number

	/** Animation speed multiplier (0.5 = half speed, 2 = double speed). Default: 1 */
	speed?: number

	/** Fade-in duration (ms). Default: 1000 */
	fadeInDuration?: number

	/** Fade-out duration (ms). Default: 8000 */
	fadeOutDuration?: number

	/** Opacity when idle/dimmed (0-1). Set to 0 to fully hide. Default: 0.6 */
	idleOpacity?: number

	/** Whether idle state continues breathing. Default: true */
	idleBreathe?: boolean

	/** Color temperature shift (-1 = cool/blue, 0 = neutral, 1 = warm/pink). Default: 0 */
	temperature?: number

	/** Chromatic aberration intensity (0-1). Default: 1 */
	chromaticAberration?: number

	/** Light-scheme treatment: false (default) = Nacre, mother-of-pearl; true = Glacial
	 *  Core, light through cut ice. No effect on dark, which always renders the deep-space
	 *  composition. Default: false */
	glacial?: boolean
}

const DEFAULTS: Required<NebulaOptions> = {
	active: true,
	autoHideDuration: 3000,
	background: true,
	intensity: 1,
	blur: 1,
	speed: 1,
	fadeInDuration: 1000,
	fadeOutDuration: 8000,
	idleOpacity: 0.6,
	idleBreathe: true,
	temperature: 0,
	chromaticAberration: 1,
	glacial: false,
}

// Cached — no function calls in the hot path
const BREATHING_EASING = 'cubic-bezier(0.37, 0, 0.63, 1)'
const DRIFT_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)'

// One sheet, adopted into every root that holds an overlay. A `<style>` in
// `document.head` never crosses a shadow boundary, and every host in this repo is inside
// one — which is why none of these keyframes had ever run.
const styledRoots = new WeakSet<Document | ShadowRoot>()
let sheet: CSSStyleSheet | null = null

function adoptStyles(root: Document | ShadowRoot): void {
	if (styledRoots.has(root)) return
	styledRoots.add(root)
	if (!sheet) {
		sheet = new CSSStyleSheet()
		sheet.replaceSync(SHEET_CSS)
	}
	root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet]
}

const SHEET_CSS = `
@keyframes nebula-chromatic-red {
	0%, 100% { transform: translate3d(var(--nebula-aberration, 3px), 0, 0); }
	25% { transform: translate3d(calc(var(--nebula-aberration, 3px) * 1.8), -1%, 0); }
	50% { transform: translate3d(var(--nebula-aberration, 3px), 1%, 0); }
	75% { transform: translate3d(calc(var(--nebula-aberration, 3px) * 0.5), 0, 0); }
}

@keyframes nebula-chromatic-blue {
	0%, 100% { transform: translate3d(calc(var(--nebula-aberration, 3px) * -1), 0, 0); }
	25% { transform: translate3d(calc(var(--nebula-aberration, 3px) * -0.5), 1%, 0); }
	50% { transform: translate3d(calc(var(--nebula-aberration, 3px) * -1), -1%, 0); }
	75% { transform: translate3d(calc(var(--nebula-aberration, 3px) * -1.8), 0, 0); }
}

/* Iridescent core - compositor-only rotation. filter is static (baked inline) to avoid
   per-frame raster cost of animated blur/hue-rotate under mix-blend-mode: screen. */
@keyframes nebula-iridescent {
	0% { transform: translateZ(0) rotate(0deg); }
	100% { transform: translateZ(0) rotate(360deg); }
}

@keyframes nebula-tendrils-breathe {
	0%, 100% { opacity: 0.6; transform: translate3d(0, 0, 0); }
	30% { opacity: 0.9; transform: translate3d(1%, -1.5%, 0); }
	60% { opacity: 0.5; transform: translate3d(-1%, 1.5%, 0); }
}


/* Idle breathing - compositor-only pulse when dimmed.
   filter: blur is intentionally static (baked on layers) — animating it here
   would keep re-rasterizing the dimmed overlay forever for no visual gain. */
@keyframes nebula-idle-breathe {
	0%, 100% {
		opacity: var(--nebula-idle-opacity, 0.08);
		transform: scale(1) translateZ(0);
	}
	50% {
		opacity: calc(var(--nebula-idle-opacity, 0.08) * 1.4);
		transform: scale(1.005) translateZ(0);
	}
}

.nebula-overlay.paused .nebula-layer,
.nebula-overlay.dimmed .nebula-layer {
	animation-play-state: paused !important;
}

.nebula-overlay.running:not(.dimmed) .nebula-layer {
	animation-play-state: running !important;
}

/* Reduced motion - respect user preferences */
@media (prefers-reduced-motion: reduce) {
	.nebula-layer {
		animation: none !important;
		transition: opacity 0s !important;
	}
}
`

type Scheme = 'dark' | 'light'

// The theme writes the standard, inherited `color-scheme` onto its host, so every
// descendant resolves the scheme it is actually painted against — including a nested
// theme that differs from the page around it.
function resolveScheme(element: HTMLElement): Scheme {
	const declared = window.getComputedStyle(element).colorScheme.trim()
	if (declared === 'dark' || declared === 'light') return declared
	return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Three skies, one painter. Every value below is the exploration board's, carried over
// literally rather than re-judged — `intensity` scales each layer's opacity so the
// colour relationships the board established survive being dimmed.
function addLayer(
	overlay: HTMLElement,
	layer: { inset?: string; background: string; blend?: string; filter?: string; opacity?: number; animation?: string },
): void {
	const el = document.createElement('div')
	el.className = 'nebula-layer'
	Object.assign(el.style, {
		position: 'absolute',
		inset: layer.inset ?? '0',
		background: layer.background,
		mixBlendMode: layer.blend ?? 'normal',
		filter: layer.filter ?? 'none',
		opacity: String(layer.opacity ?? 1),
		transform: 'translateZ(0)',
		animation: layer.animation ?? 'none',
	})
	overlay.appendChild(el)
}

// Dark — light emerging from a void. `screen` over near-black; the void layer paints
// straight (no blend) because it IS the darkness the rest emerges from.
function paintNightSky(overlay: HTMLElement, opts: Required<NebulaOptions>, reducedMotion: boolean): void {
	const warm = Math.max(0, opts.temperature)
	const cool = Math.max(0, -opts.temperature)
	const I = opts.intensity
	const drift = 35000 / opts.speed
	const red = { r: 255, g: Math.round(20 + cool * 60), b: Math.round(80 + cool * 80) }
	const blue = { r: Math.round(20 + warm * 80), g: 100, b: Math.round(255 - warm * 55) }
	const pink = { r: 255, g: Math.round(100 + cool * 100), b: 200 }
	const cyan = { r: Math.round(warm * 100), g: Math.round(255 - warm * 55), b: 200 }

	addLayer(overlay, {
		opacity: I,
		background: `
			radial-gradient(ellipse 90% 80% at 50% 50%, transparent 30%, rgba(0,0,0,0.55) 90%),
			linear-gradient(155deg, transparent 40%, rgba(8,2,18,0.6) 48%, transparent 56%)`,
	})

	// The clouds are painted on a box far larger than the card and then clipped by it, so
	// their falloff completes off-frame and the colour reads as vanishing past the edge
	// rather than stopping at it. The void layer stays at the card's own bounds — its
	// vignette and band are the board's geometry and only land right at 1:1.
	addLayer(overlay, {
		inset: '-38%',
		blend: 'screen',
		filter: `blur(${20 * opts.blur}px)`,
		opacity: I,
		animation: reducedMotion ? 'none' : `nebula-chromatic-red ${drift}ms ${DRIFT_EASING} infinite`,
		background: `radial-gradient(ellipse 45% 55% at 40% 45%,
			rgba(${red.r},${red.g},${red.b},0.5) 0%,
			rgba(${red.r},${red.g},${red.b},0.18) 40%,
			transparent 68%)`,
	})

	addLayer(overlay, {
		inset: '-38%',
		blend: 'screen',
		filter: `blur(${20 * opts.blur}px)`,
		opacity: I,
		animation: reducedMotion ? 'none' : `nebula-chromatic-blue ${drift}ms ${DRIFT_EASING} infinite`,
		background: `radial-gradient(ellipse 42% 50% at 62% 55%,
			rgba(${blue.r},${blue.g},${blue.b},0.45) 0%,
			rgba(${blue.r},${blue.g},${blue.b},0.16) 42%,
			transparent 68%)`,
	})

	addLayer(overlay, {
		blend: 'screen',
		filter: `blur(${6 * opts.blur}px)`,
		opacity: I,
		background: `radial-gradient(circle 8% at 50% 50%,
			rgba(255,255,255,0.9) 0%,
			rgba(255,240,245,0.4) 45%,
			transparent 100%)`,
	})

	addLayer(overlay, {
		inset: '-30%',
		blend: 'screen',
		filter: `blur(${14 * opts.blur}px)`,
		opacity: I,
		animation: reducedMotion ? 'none' : `nebula-tendrils-breathe ${22000 / opts.speed}ms ${BREATHING_EASING} infinite`,
		background: `
			radial-gradient(ellipse 55% 10% at 50% 48%, rgba(${pink.r},${pink.g},${pink.b},0.35) 0%, transparent 70%),
			radial-gradient(ellipse 10% 50% at 48% 50%, rgba(${cyan.r},${cyan.g},${cyan.b},0.3) 0%, transparent 65%)`,
	})

}

// Light, default — mother-of-pearl. `mix-blend-mode: color` lays hue across the
// surface's own highlights and shadows instead of on top of them, which is why the
// pastel never separates from the ground the way a flat wash does.
function paintNacre(overlay: HTMLElement, opts: Required<NebulaOptions>): void {
	const I = opts.intensity

	addLayer(overlay, {
		blend: 'multiply',
		opacity: I,
		background: `radial-gradient(ellipse 100% 100% at 50% 50%, rgba(95,82,74,0.08) 0%, rgba(95,82,74,0.18) 100%)`,
	})
	addLayer(overlay, {
		blend: 'color',
		filter: `blur(${2 * opts.blur}px) saturate(1.2)`,
		opacity: I,
		background: `conic-gradient(from 200deg at 44% 42%, #ff8fc0, #ffd98f, #93e8ba, #82c2ff, #c48fff, #ff8fc0)`,
	})
	addLayer(overlay, {
		blend: 'color',
		filter: `blur(${3 * opts.blur}px)`,
		opacity: 0.55 * I,
		background: `conic-gradient(from 20deg at 68% 62%, #ffe0a0, #a0f0c8, #a0d0ff, #e0a0ff, #ffe0a0)`,
	})
	addLayer(overlay, {
		opacity: I,
		background: `radial-gradient(ellipse 30% 22% at 26% 22%, rgba(255,255,255,0.85) 0%, transparent 68%)`,
	})
	addLayer(overlay, {
		opacity: I,
		background: `linear-gradient(112deg, transparent 40%, rgba(255,255,255,0.4) 46%, rgba(255,255,255,0.5) 48%, transparent 54%)`,
	})
}

// Light, opt-in — light through cut ice. Depth from refraction rather than void:
// faceted planes multiply cool shadow in, `color-dodge` glints flash where a facet
// catches the light straight on.
function paintGlacial(overlay: HTMLElement, opts: Required<NebulaOptions>): void {
	const I = opts.intensity

	addLayer(overlay, {
		blend: 'multiply',
		opacity: I,
		background: `
			linear-gradient(115deg, transparent 0%, transparent 18%, rgba(70,110,140,0.17) 29%, transparent 41%),
			linear-gradient(70deg, transparent 0%, transparent 33%, rgba(90,130,160,0.14) 45%, transparent 58%),
			linear-gradient(150deg, transparent 0%, transparent 53%, rgba(60,95,125,0.19) 65%, transparent 78%),
			linear-gradient(95deg, transparent 0%, transparent 68%, rgba(130,90,150,0.10) 78%, transparent 88%)`,
	})
	addLayer(overlay, {
		blend: 'color',
		opacity: 0.85 * I,
		background: `
			radial-gradient(ellipse 65% 55% at 45% 45%, #d8f4ff 0%, #eef8ff 45%, transparent 72%),
			radial-gradient(ellipse 26% 22% at 80% 78%, #f4e0ff 0%, transparent 65%),
			radial-gradient(ellipse 20% 18% at 22% 78%, #ffe8d8 0%, transparent 60%)`,
	})
	addLayer(overlay, {
		blend: 'color-dodge',
		filter: `blur(${1 * opts.blur}px)`,
		opacity: 0.6 * I,
		background: `
			linear-gradient(115deg, transparent 26%, rgba(255,255,255,0.95) 28%, transparent 30%),
			linear-gradient(70deg, transparent 42%, rgba(255,255,255,0.75) 44%, transparent 46%),
			linear-gradient(150deg, transparent 62%, rgba(215,235,255,0.8) 64%, transparent 66%)`,
	})
	addLayer(overlay, {
		blend: 'multiply',
		opacity: I,
		background: `radial-gradient(ellipse 90% 90% at 50% 50%, transparent 46%, rgba(50,80,105,0.12) 92%)`,
	})
}

function paintLayers(
	overlay: HTMLElement,
	opts: Required<NebulaOptions>,
	reducedMotion: boolean,
	scheme: Scheme,
): void {
	if (scheme === 'dark') {
		paintNightSky(overlay, opts, reducedMotion)
		return
	}
	if (opts.glacial) {
		paintGlacial(overlay, opts)
		return
	}
	paintNacre(overlay, opts)
}

interface State {
	element: HTMLElement
	overlay: HTMLElement | null
	originalPosition: string
	originalOverflow: string
	originalContain: string
	isDimmed: boolean
	autoHideSub: Subscription | null
	fadeFinishedSub: Subscription | null
	schemeSub: Subscription | null
	paintSub: Subscription | null
	options: Required<NebulaOptions>
	reducedMotion: boolean
	scheme: Scheme
	isVisible: boolean
}

class NebulaCoordinator {
	static #instance: NebulaCoordinator | null = null
	private elementToDirective = new WeakMap<HTMLElement, NebulaDirective>()
	private instances = new Set<NebulaDirective>()
	private observer: IntersectionObserver | null = null
	private visibilitySub: Subscription | null = null
	private tabVisible = true

	static get instance(): NebulaCoordinator {
		if (!NebulaCoordinator.#instance) {
			NebulaCoordinator.#instance = new NebulaCoordinator()
		}
		return NebulaCoordinator.#instance
	}

	register(dir: NebulaDirective, element: HTMLElement): void {
		const wasEmpty = this.instances.size === 0
		this.instances.add(dir)
		this.elementToDirective.set(element, dir)

		if (wasEmpty) {
			this.setup()
		}

		this.observer?.observe(element)
	}

	unregister(dir: NebulaDirective, element: HTMLElement): void {
		this.observer?.unobserve(element)
		this.elementToDirective.delete(element)
		this.instances.delete(dir)

		if (this.instances.size === 0) {
			this.teardown()
		}
	}

	private setup(): void {
		this.observer = new IntersectionObserver(
			entries => {
				for (const entry of entries) {
					const dir = this.elementToDirective.get(entry.target as HTMLElement)
					if (dir) {
						dir.onVisibilityChange(entry.isIntersecting && this.tabVisible)
					}
				}
			},
			{ threshold: 0 },
		)

		this.visibilitySub = fromEvent(document, 'visibilitychange')
			.pipe(
				tap(() => {
					this.tabVisible = document.visibilityState === 'visible'
					for (const dir of this.instances) {
						dir.onVisibilityChange(this.tabVisible)
					}
				}),
			)
			.subscribe()
	}

	private teardown(): void {
		if (this.observer) {
			this.observer.disconnect()
			this.observer = null
		}
		if (this.visibilitySub) {
			this.visibilitySub.unsubscribe()
			this.visibilitySub = null
		}
	}
}

class NebulaDirective extends AsyncDirective {
	private state: State | null = null
	private coordinator = NebulaCoordinator.instance

	render(options?: NebulaOptions) {
		void options
		return noChange
	}

	override update(part: ElementPart, [options = {}]: [NebulaOptions?]) {
		const element = part.element as HTMLElement
		const opts = { ...DEFAULTS, ...options }

		if (opts.active) {
			this.show(element, opts)
		} else {
			this.hide(opts)
		}

		return noChange
	}

	// Called by coordinator. Toggles a CSS class rather than calling a JS
	// animation API — cheaper and lets the CSS `animation-play-state` rule own it.
	onVisibilityChange(isVisible: boolean): void {
		if (!this.state?.overlay) return

		const wasVisible = this.state.isVisible
		this.state.isVisible = isVisible

		if (wasVisible !== isVisible) {
			this.state.overlay.classList.toggle('paused', !isVisible)
			this.state.overlay.classList.toggle('running', isVisible)
		}
	}

	private show(element: HTMLElement, opts: Required<NebulaOptions>): void {
		if (this.state?.autoHideSub) {
			this.state.autoHideSub.unsubscribe()
			this.state.autoHideSub = null
		}

		const reducedMotion = reducedMotion$.value

		// Already showing but dimmed → reawaken in place
		if (this.state?.overlay && this.state.isDimmed) {
			this.state.isDimmed = false
			this.state.options = opts
			this.state.fadeFinishedSub?.unsubscribe()
			this.state.fadeFinishedSub = null
			this.awakenOverlay(opts, reducedMotion)
			this.scheduleAutoHide(opts)
			return
		}

		// Already showing and active
		if (this.state?.overlay) {
			this.scheduleAutoHide(opts)
			return
		}

		this.createOverlay(element, opts, reducedMotion)
		this.scheduleAutoHide(opts)
	}

	private awakenOverlay(opts: Required<NebulaOptions>, reducedMotion: boolean): void {
		if (!this.state?.overlay) return

		const overlay = this.state.overlay
		overlay.style.setProperty('--nebula-intensity', String(opts.intensity))

		overlay.animate(
			[
				{ opacity: opts.idleOpacity, transform: 'scale(0.98) translateZ(0)' },
				{ opacity: opts.intensity * 0.7, transform: 'scale(1.01) translateZ(0)' },
				{ opacity: opts.intensity, transform: 'scale(1) translateZ(0)' },
			] as Keyframe[],
			{
				duration: reducedMotion ? 0 : opts.fadeInDuration * 0.6,
				easing: reducedMotion ? 'linear' : getEasing(SPRING_SMOOTH),
				fill: 'forwards',
			},
		)

		// Clear the dimmed gate + any container-level idle-breathe animation hide() installed,
		// then resume the per-layer CSS animations.
		overlay.classList.remove('paused', 'dimmed')
		overlay.classList.add('running')
		overlay.style.animation = ''
	}

	private createOverlay(element: HTMLElement, opts: Required<NebulaOptions>, reducedMotion: boolean): void {
		const computedStyle = window.getComputedStyle(element)
		const computedPosition = computedStyle.position

		const originalPosition = element.style.position
		const originalOverflow = element.style.overflow
		const originalContain = element.style.contain

		if (computedPosition === 'static') {
			element.style.position = 'relative'
		}
		// clip, not hidden: the layers overhang the host, and a hidden host is still a
		// scroll container that focus() or scrollIntoView() will scroll sideways.
		element.style.overflow = 'clip'
		element.style.contain = 'paint'

		const I = opts.intensity // shorthand
		const aberrationPx = Math.round(3 + opts.chromaticAberration * 5)

		const overlay = document.createElement('div')
		overlay.className = 'nebula-overlay running'
		Object.assign(overlay.style, {
			position: 'absolute',
			inset: '0',
			zIndex: opts.background ? '-1' : '9999',
			opacity: '0',
			// Inline, not in the injected sheet: the overlay is appended into whatever
			// root its host lives in, and a document-head rule never crosses a shadow
			// boundary — the class alone leaves the decoration hit-testable.
			pointerEvents: 'none',
			'--nebula-intensity': String(I),
			'--nebula-blur-base': `${opts.blur * 10}px`,
			'--nebula-idle-opacity': String(opts.idleOpacity),
			'--nebula-aberration': `${aberrationPx}px`,
			'--nebula-blur-core': `${12 * opts.blur}px`,
		})

		element.appendChild(overlay)

		const fadeInDuration = reducedMotion ? 0 : opts.fadeInDuration
		const entranceEasing = reducedMotion ? 'linear' : getEasing(SPRING_SMOOTH)

		// Compositor-only entrance — opacity + transform reach the same "rising into focus"
		// feel without animating `filter: blur`, which re-rasterizes the full filtered
		// region every frame under mix-blend-mode: screen.
		overlay.animate(
			[
				{ opacity: 0, transform: 'scale(0.85) translateZ(0)' },
				{ opacity: opts.intensity * 0.4, transform: 'scale(0.97) translateZ(0)' },
				{ opacity: opts.intensity, transform: 'scale(1) translateZ(0)' },
			] as Keyframe[],
			{
				duration: fadeInDuration,
				easing: entranceEasing,
				fill: 'forwards',
			},
		)

		this.state = {
			element,
			overlay,
			originalPosition,
			originalOverflow,
			originalContain,
			isDimmed: false,
			autoHideSub: null,
			fadeFinishedSub: null,
			schemeSub: null,
			paintSub: null,
			options: opts,
			reducedMotion,
			scheme: 'dark',
			isVisible: document.visibilityState === 'visible',
		}

		// Lit runs an ElementPart directive's update() before it inserts the element, so on
		// first render the host is detached: it inherits no `color-scheme` and its root is a
		// throwaway fragment. Both answers are wrong, and wrong permanently — nothing
		// re-asks once the tree settles. The frame boundary is what makes them right.
		this.state.paintSub = of(null)
			.pipe(
				observeOn(animationFrameScheduler),
				tap(() => this.paintWhenConnected()),
				catchError(() => EMPTY),
			)
			.subscribe()

		// The mutation that writes `color-scheme` is the signal. A theme service is not: it
		// reports app intent, which under `auto` already equals the resolved value, so
		// distinctUntilChanged swallows exactly the emission that matters.
		this.state.schemeSub = merge(
			new Observable<unknown>(subscriber => {
				const observer = new MutationObserver(() => subscriber.next(0))
				observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'], subtree: true })
				return () => observer.disconnect()
			}),
			fromEvent(window.matchMedia('(prefers-color-scheme: dark)'), 'change'),
		)
			.pipe(
				observeOn(animationFrameScheduler),
				tap(() => this.paintWhenConnected()),
				catchError(() => EMPTY),
			)
			.subscribe()

		this.coordinator.register(this, element)
	}

	// Paints against the scheme the host actually resolves, into the root that actually
	// holds the overlay. Safe to call repeatedly: it repaints only when the answer changed
	// or when nothing has been painted yet.
	private paintWhenConnected(): void {
		const state = this.state
		if (!state?.overlay || !state.element.isConnected) return

		const root = state.overlay.getRootNode()
		if (root instanceof Document || root instanceof ShadowRoot) adoptStyles(root)

		const next = resolveScheme(state.element)
		if (next === state.scheme && state.overlay.childElementCount > 0) return

		state.scheme = next
		state.overlay.replaceChildren()
		paintLayers(state.overlay, state.options, state.reducedMotion, next)
	}

	private scheduleAutoHide(opts: Required<NebulaOptions>): void {
		if (!this.state || opts.autoHideDuration <= 0) return

		this.state.autoHideSub = timer(opts.autoHideDuration)
			.pipe(
				take(1),
				tap(() => {
					if (this.state) {
						this.state.autoHideSub = null
						this.hide(this.state.options)
					}
				}),
			)
			.subscribe()
	}

	private hide(opts: Required<NebulaOptions>): void {
		if (!this.state?.overlay) return

		if (this.state.autoHideSub) {
			this.state.autoHideSub.unsubscribe()
			this.state.autoHideSub = null
		}

		if (this.state.isDimmed) return

		this.state.isDimmed = true
		const overlay = this.state.overlay
		const currentOpts = this.state.options
		const reducedMotion = this.state.reducedMotion
		const duration = reducedMotion ? 0 : opts.fadeOutDuration
		const easing = reducedMotion ? 'linear' : getEasing(SPRING_GENTLE)

		if (opts.idleOpacity <= 0) {
			overlay.animate(
				[
					{ opacity: currentOpts.intensity, transform: 'scale(1) translateZ(0)' },
					{ opacity: currentOpts.intensity * 0.4, transform: 'scale(0.95) translateZ(0)' },
					{ opacity: 0, transform: 'scale(0.9) translateZ(0)' },
				] as Keyframe[],
				{ duration, easing, fill: 'forwards' },
			)
			return
		}

		const fadeDown = overlay.animate(
			[
				{ opacity: currentOpts.intensity, transform: 'scale(1) translateZ(0)' },
				{ opacity: currentOpts.intensity * 0.5, transform: 'scale(0.99) translateZ(0)' },
				{ opacity: opts.idleOpacity, transform: 'scale(1) translateZ(0)' },
			] as Keyframe[],
			{ duration, easing, fill: 'forwards' },
		)

		// After fade-down, gate per-layer animations off via the `dimmed` class — without
		// this, chromatic/iridescent/tendril/particle keyframes keep burning GPU under
		// mix-blend-mode: screen forever even though the stack is visually idle.
		this.state.fadeFinishedSub?.unsubscribe()
		this.state.fadeFinishedSub = from(fadeDown.finished)
			.pipe(
				take(1),
				tap(() => {
					if (!this.state?.overlay) return
					this.state.overlay.classList.add('dimmed')
					if (opts.idleBreathe && !reducedMotion) {
						this.state.overlay.style.animation = `nebula-idle-breathe ${12000 / currentOpts.speed}ms ${BREATHING_EASING} infinite`
					}
				}),
				catchError(() => EMPTY),
			)
			.subscribe()
	}

	private cleanup(): void {
		if (!this.state) return

		this.coordinator.unregister(this, this.state.element)

		this.state.autoHideSub?.unsubscribe()
		this.state.fadeFinishedSub?.unsubscribe()
		this.state.schemeSub?.unsubscribe()
		this.state.paintSub?.unsubscribe()

		this.state.overlay?.remove()
		this.state.element.style.position = this.state.originalPosition
		this.state.element.style.overflow = this.state.originalOverflow
		this.state.element.style.contain = this.state.originalContain
		this.state = null
	}

	override disconnected(): void {
		this.cleanup()
	}

	override reconnected(): void {
		if (this.state) {
			this.coordinator.register(this, this.state.element)
		}
	}
}

export const nebula = directive(NebulaDirective)
